// Tumanasi Delivery Service — API client
// Connects to backend_dexter API at /api/tumanasi/*

import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4001/api';

const api = axios.create({ baseURL: API_BASE });

// Auth: use localStorage token
api.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('token');
    if (token) {
        cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
});

// Error handling: display errors as toasts
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Don't show toast for auth endpoints - let components handle them
        const isAuthEndpoint = error.config?.url?.includes('/auth/');

        if (!isAuthEndpoint && error.response?.data?.detail) {
            // Backend returned a specific error message
            toast.error(error.response.data.detail);
        } else if (!isAuthEndpoint && error.response?.status === 500) {
            toast.error('Server error. Please try again later.');
        } else if (!isAuthEndpoint && error.response?.status === 404) {
            toast.error('Resource not found.');
        } else if (!isAuthEndpoint && !error.response) {
            toast.error('Network error. Please check your connection.');
        }

        return Promise.reject(error);
    }
);

export const tumansiApi = {
    // ── Zones ──────────────────────────────────────────────────
    listZones: () => api.get('/tumanasi/zones'),
    searchZones: (q) => api.get('/tumanasi/zones/search', { params: { q } }),
    getZone: (id) => api.get(`/tumanasi/zones/${id}`),

    // ── Customer deliveries ────────────────────────────────────
    bookDelivery: (body) => api.post('/tumanasi/deliveries', body),
    track: (num) => api.get(`/tumanasi/deliveries/track/${num}`),
    myDeliveries: () => api.get('/tumanasi/deliveries/my'),
    getDelivery: (id) => api.get(`/tumanasi/deliveries/${id}`),
    cancelDelivery: (id, reason) => api.post(`/tumanasi/deliveries/${id}/cancel`, { reason }),
    rateRider: (id, body) => api.post(`/tumanasi/deliveries/${id}/rate`, body),
    initiatePayment: (id, body) => api.post(`/tumanasi/deliveries/${id}/pay`, body),

    // ── Rider ──────────────────────────────────────────────────
    registerRider: (body) => api.post('/tumanasi/rider/register', body),
    getRiderProfile: () => api.get('/tumanasi/rider/me'),
    updateRider: (body) => api.put('/tumanasi/rider/me', body),
    setAvailability: (available) => api.put('/tumanasi/rider/availability', { is_available: available }),
    riderDeliveries: (status) => api.get('/tumanasi/rider/deliveries', { params: status ? { status } : {} }),
    riderLogClientDelivery: (body) => api.post('/tumanasi/rider/log-delivery', body),
    availableJobs: () => api.get('/tumanasi/rider/deliveries/available'),
    acceptDelivery: (id) => api.put(`/tumanasi/rider/deliveries/${id}/accept`),
    updateStatus: (id, status, note) => api.put(`/tumanasi/rider/deliveries/${id}/status`, { status, note }),
    confirmCash: (id) => api.put(`/tumanasi/rider/deliveries/${id}/confirm-payment`),

    uploadPickupPhoto: (id, formData) =>
        api.post(`/tumanasi/rider/deliveries/${id}/pickup-photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),

    uploadDeliveryPhoto: (id, formData) =>
        api.post(`/tumanasi/rider/deliveries/${id}/delivery-photo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),

    // ── Admin ──────────────────────────────────────────────────
    adminStats: () => api.get('/tumanasi/admin/stats'),
    adminDeliveries: (params) => api.get('/tumanasi/admin/deliveries', { params }),
    adminCreateDelivery: (body) => api.post('/tumanasi/admin/deliveries', body),
    adminRiders: (params) => api.get('/tumanasi/admin/riders', { params }),
    verifyRider: (id, body) => api.put(`/tumanasi/admin/riders/${id}/verify`, body),
    adminCreateRider: (body) => api.post('/tumanasi/admin/riders', body),
    adminResetRiderPassword: (id, body) => api.put(`/tumanasi/admin/riders/${id}/reset-password`, body),
    updateDelivery: (id, body) => api.put(`/tumanasi/admin/deliveries/${id}`, body),
    assignRider: (delivId, riderId) =>
        api.put(`/tumanasi/admin/deliveries/${delivId}/assign`, { rider_id: riderId }),
    createZone: (body) => api.post('/tumanasi/admin/zones', body),
    updateZone: (id, body) => api.put(`/tumanasi/admin/zones/${id}`, body),
    deleteZone: (id) => api.delete(`/tumanasi/admin/zones/${id}`),
    availableRiders: () => api.get('/tumanasi/admin/riders/available'),

    // ── Admin: Expenses ────────────────────────────────────────
    adminExpenses: (params) => api.get('/tumanasi/admin/expenses', { params }),
    adminCreateExpense: (body) => api.post('/tumanasi/admin/expenses', body),
    adminUpdateExpense: (id, body) => api.put(`/tumanasi/admin/expenses/${id}`, body),
    adminDeleteExpense: (id) => api.delete(`/tumanasi/admin/expenses/${id}`),

    // ── Admin: Rider Payments ──────────────────────────────────
    adminRiderPayments: (params) => api.get('/tumanasi/admin/rider-payments', { params }),
    adminCreateRiderPayment: (body) => api.post('/tumanasi/admin/rider-payments', body),
    adminUpdateRiderPayment: (id, body) => api.put(`/tumanasi/admin/rider-payments/${id}`, body),
    adminDeleteRiderPayment: (id) => api.delete(`/tumanasi/admin/rider-payments/${id}`),
};
